<template>
  <div v-if="normalizedItems.length" class="right-nav-items">
    <span
      v-for="(item, index) in normalizedItems"
      :key="`right-nav-${index}`"
      class="right-nav-item-wrap"
    >
      <el-popover
        v-if="item.responseType === 'popoverPage' && item.popoverPageData"
        :placement="item.popoverPlacement || 'right'"
        :trigger="item.popoverTrigger || 'hover'"
        :width="item.popoverWidth || 400"
        :popper-style="getPopoverStyle(item)"
        :popper-class="item.popupClass || ''"
      >
        <template #reference>
          <span
            class="right-nav-item right-nav-item--interactive"
            :class="itemClass"
            :style="getItemStyle(item)"
          >
            <LazyImage
              v-if="item.icon && item.iconPosition !== 'after'"
              class="right-nav-item__icon"
              :src="item.icon"
              :style="getIconStyle(item)"
              :use-lazy="false"
              :tint-color="getIconTintColor(item)"
              alt=""
            />
            <span v-if="item.title" class="right-nav-item__text">
              {{ getLocaleText(item.title) }}
            </span>
            <LazyImage
              v-if="item.icon && item.iconPosition === 'after'"
              class="right-nav-item__icon"
              :src="item.icon"
              :style="getIconStyle(item)"
              :use-lazy="false"
              :tint-color="getIconTintColor(item)"
              alt=""
            />
          </span>
        </template>
        <PopoverPageRender
          :area-data="item.popoverPageData"
          :width="item.popoverWidth || 400"
          :background-color="item.popoverBackgroundColor || '#ffffff'"
          :border-radius="getPopoverBorderRadius(item)"
        />
      </el-popover>

      <component
        v-else
        :is="itemTag(item)"
        class="right-nav-item"
        :class="[
          itemClass,
          {
            'right-nav-item--interactive': item.responseType === 'popup',
          },
        ]"
        :style="getItemStyle(item)"
        :href="getItemHref(item)"
        :to="getItemTo(item)"
        :target="getItemTarget(item)"
        @click="onItemClick(item, index, $event)"
      >
        <LazyImage
          v-if="item.icon && item.iconPosition !== 'after'"
          class="right-nav-item__icon"
          :src="item.icon"
          :style="getIconStyle(item)"
          :use-lazy="false"
          :tint-color="getIconTintColor(item)"
          alt=""
        />
        <span v-if="item.title" class="right-nav-item__text">
          {{ getLocaleText(item.title) }}
        </span>
        <LazyImage
          v-if="item.icon && item.iconPosition === 'after'"
          class="right-nav-item__icon"
          :src="item.icon"
          :style="getIconStyle(item)"
          :use-lazy="false"
          :tint-color="getIconTintColor(item)"
          alt=""
        />
      </component>

      <el-dialog
        v-if="item.responseType === 'popup'"
        :class="item.popupClass || ''"
        :title="item.popupTitle || ''"
        append-to-body
        v-model="dialogVisibleMap[index]"
      >
        <template #default>
          <div
            v-if="dialogVisibleMap[index]"
            class="new-detail-item-cont"
            v-html="articleContentMap[index]?.content"
          ></div>
        </template>
        <template #footer>
          <el-button type="primary" @click="dialogVisibleMap[index] = false">
            {{ getLocaleText("关闭") }}
          </el-button>
        </template>
      </el-dialog>
    </span>
  </div>
</template>

<script setup>
import { computed, reactive, watch } from "vue";
import { useRoute } from "#app";
import { NuxtLink } from "#components";
import { getArticleDetails } from "@/api";
import { getSafeLink, isTarget_Blank } from "@/utils/globalData.js";
import { legacyPathToUserCenterPath } from "~/user-center/routes";
import PopoverPageRender from "@/components/render/PopoverPageRender.vue";
import LazyImage from "@/components/common/LazyImage/index.vue";
 
const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  getLocaleText: {
    type: Function,
    required: true,
  },
  itemClass: {
    type: String,
    default: "",
  },
  themeVars: {
    type: Object,
    default: () => ({}),
  },
});

const dialogVisibleMap = reactive({});
const articleContentMap = reactive({});
const route = useRoute();
const normalizedItems = computed(() => {
  return (props.items || []).map((item) => ({
    iconPosition: "before",
    responseType: "link",
    link: "",
    popupTitle: "",
    popupClass: "",
    popoverPageData: "",
    popoverWidth: 400,
    popoverBackgroundColor: "",
    popoverBorderRadius: 6,
    popoverPlacement: "right",
    popoverTrigger: "hover",
    fontColor: "",
    fontSize: undefined,
    iconSize: undefined,
    iconTintColor: "",
    iconActiveTintColor: "",
    ...item,
  }));
});

function getItemStyle(item) {
  const style = {};
  if (item?.fontColor) {
    style.color = item.fontColor;
  }
  if (typeof item?.fontSize === "number") {
    style.fontSize = `${item.fontSize}px`;
  }
  if (!style.color && props.themeVars?.navTextColor) {
    style.color = props.themeVars.navTextColor;
  }
  return style;
}

function getIconStyle(item) {
  const size =
    typeof item?.iconSize === "number" && item.iconSize > 0
      ? item.iconSize
      : undefined;
  if (!size) {
    return undefined;
  }
  return {
    width: `${size}px`,
    height: `${size}px`,
  };
}

function getPopoverBorderRadius(item) {
  return typeof item?.popoverBorderRadius === "number"
    ? item.popoverBorderRadius
    : 6;
}

function getPopoverStyle(item) {
  return {
    padding: "0px",
    border: "none",
    overflow: "hidden",
    borderRadius: `${getPopoverBorderRadius(item)}px`,
    backgroundColor: item?.popoverBackgroundColor || "#ffffff",
  };
}

function normalizeComparablePath(rawPath = "") {
  const input = String(rawPath || "").trim();
  if (!input) {
    return "";
  }
  if (/^(https?:)?\/\//.test(input)) {
    return input;
  }
  const normalizedInput = legacyPathToUserCenterPath(input);
  try {
    const url = new URL(normalizedInput, "http://localhost");
    const pathname = url.pathname !== "/" ? url.pathname.replace(/\/+$/, "") : "/";
    return `${pathname}${url.search}${url.hash}`;
  } catch {
    return normalizedInput;
  }
}

function getMatchScore(currentFullPath, linkUrl) {
  if (!linkUrl || /^(https?:)?\/\//.test(linkUrl)) {
    return -1;
  }
  const normalizedCurrent = normalizeComparablePath(currentFullPath || "/");
  const normalizedLink = normalizeComparablePath(linkUrl);
  if (!normalizedCurrent || !normalizedLink) {
    return -1;
  }
  if (normalizedLink === "/") {
    return normalizedCurrent === "/" ? 100 : -1;
  }
  if (normalizedCurrent === normalizedLink) {
    return 1000 + normalizedLink.length;
  }
  if (normalizedLink.startsWith("/v?url")) {
    return -1;
  }
  const [currentPathname] = normalizedCurrent.split(/[?#]/);
  const [linkPathname] = normalizedLink.split(/[?#]/);
  if (linkPathname && currentPathname === linkPathname) {
    return 800 + linkPathname.length;
  }
  if (linkPathname && currentPathname.startsWith(`${linkPathname}/`)) {
    return 600 + linkPathname.length;
  }
  if (linkPathname && normalizedCurrent.startsWith(`${linkPathname}?`)) {
    return 500 + linkPathname.length;
  }
  return -1;
}

function isItemActive(item) {
  if (!["link", "jumpLink"].includes(item?.responseType || "link")) {
    return false;
  }
  return getMatchScore(route.fullPath || "/", item?.link) >= 0;
}

function getIconTintColor(item) {
  if (!item?.icon) {
    return "";
  }
  if (isItemActive(item)) {
    return item?.iconActiveTintColor || item?.iconTintColor || "";
  }
  return item?.iconTintColor || "";
}

function getItemHref(item) {
  if (!["link", "jumpLink"].includes(item?.responseType || "link")) {
    return undefined;
  }
  const link = getSafeLink(item?.link);
  if (!/^(https?:)?\/\//.test(link)) {
    return undefined;
  }
  return link;
}

function getItemTo(item) {
  if (!["link", "jumpLink"].includes(item?.responseType || "link")) {
    return undefined;
  }
  const link = String(item?.link || "").trim();
  if (!link || /^(https?:)?\/\//.test(link)) {
    return undefined;
  }
  return legacyPathToUserCenterPath(link);
}

function getItemTarget(item) {
  switch (item?.responseType) {
    case "jumpLink":
      return isTarget_Blank();
    default:
      return undefined;
  }
}

function itemTag(item) {
  if (!["link", "jumpLink"].includes(item?.responseType || "link")) {
    return "span";
  }
  const link = String(item?.link || "").trim();
  if (!link) {
    return "span";
  }
  if (/^(https?:)?\/\//.test(link)) {
    return "a";
  }
  return NuxtLink;
}

async function getArticleContent(item, index) {
  if (item?.responseType !== "popup" || !item?.article?.id) {
    articleContentMap[index] = null;
    return;
  }
  const res = await getArticleDetails({
    pageNo: 0,
    pageSize: 9999,
    id: item.article.id,
    status: "1",
  });
  if (res.data.dataList && res.data.dataList[0]) {
    const article = res.data.dataList[0];
    if (article.type === 1 && article.content) {
      article.content = article.content.replace("video", "video autoplay ");
    }
    articleContentMap[index] = article;
  } else {
    articleContentMap[index] = null;
  }
}

function onItemClick(item, index, event) {
  if (item?.responseType !== "popup") {
    return;
  }
  event.preventDefault();
  dialogVisibleMap[index] = true;
  if (!articleContentMap[index]) {
    getArticleContent(item, index).catch((error) => {
      console.error("get right nav article content error", error);
      articleContentMap[index] = null;
    });
  }
}

watch(
  normalizedItems,
  (items) => {
    items.forEach((item, index) => {
      if (typeof dialogVisibleMap[index] !== "boolean") {
        dialogVisibleMap[index] = false;
      }
      if (item?.responseType === "popup" && item?.article?.id) {
        getArticleContent(item, index).catch((error) => {
          console.error("init right nav article content error", error);
          articleContentMap[index] = null;
        });
      } else {
        articleContentMap[index] = null;
      }
    });
  },
  { deep: true, immediate: true }
);
</script>

<style scoped lang="less">
.right-nav-items {
  display: inline-flex;
  align-items: center;
  gap: 16px;
  flex: 0 0 auto;
}

.right-nav-item-wrap {
  display: inline-flex;
  align-items: center;
}

.right-nav-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: inherit;
  text-decoration: none;
  line-height: 1;
}

.right-nav-item--interactive {
  cursor: pointer;
}

.right-nav-item__icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
  flex: 0 0 auto;
}

.right-nav-item__text {
  white-space: nowrap;
}
</style>
